import { pulse, seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, cardName, faceArt, fade, fill, place, playerColor, sprite,
} from './common';
import { ARROW_PARTS, arrowEnds, arrowPrims } from './attackArrow';
import {
  FRAME_URL, PANEL, miniBox, paintPanel, panelReady, panelSpec,
} from './battlePanel';

/**
 * **攻撃宣言**（Issue #552 v5。指示書 `../direction/attack.md`）。
 *
 * **盤の矢印**と**画面の真ん中の対戦の表示**（これがメイン）。まだ宣言なので**斬撃は出さない**（斬撃はダメージ計算＝ `battle`）。
 * 直接攻撃も出方は同じで、**対象のパネルが無いだけ**。
 *
 * ```
 * 0     攻撃するカードの縁から対象のカードの縁へ、鋼の矢じり（画像。`../fx/attackArrow.js`）が伸びる
 *       （直接攻撃は相手の陣地の奥を指す）。照準は出さない（ユーザー 2026-09-26）
 * 240   矢印は盤に置く印（`attackArrow`）に引き継ぐ → バトルが終わるまで盤に残る
 * 200   画面を暗くし、左右から鋼のパネルが滑り込む（攻撃する人の側に攻撃モンスター、反対に対象）
 * 440   ぶつかって止まる → ヒットストップ 100ms（色収差）→ 揺れ
 * 560   **向きの矢じり**（盤の矢印と同じ画像）が攻撃側のパネルの上から対象のパネルへ飛び、2 枚の間に突き刺さる
 *       （攻撃側のパネルは踏み込み、対象のパネルは押される。硬い火花・揺れ）。VS の代わりに「どちらがどちらへ」を見せる
 * 640〜 見得
 * 1420  パネルは縮みながらそれぞれの**小さいパネルの場所**（魔法・罠ゾーンの外側。置ける最大の大きさ）へ移る（文字も残す）
 * 1700  盤に置く印（`battlePanel`）に引き継ぐ → バトルが終わるまで残る（消えるのは矢印と同じ）
 * ```
 *
 * パネルの絵は `../fx/battlePanel.js` の 2D canvas の描き方 1 つで、three はそれをテクスチャにして貼るだけ（印も同じ描き方）。
 * ATK / DEF・レベル・表示形式は分岐レイヤー（`editorPorts/duelSituations/attack.js`）が盤面から引いて payload の `attacker` / `target` に入れる
 * （`code` が入っていると器が絵の到着で組み直してくれる）。直接攻撃かどうかも状況（`direct` / `monster`）で決まる（Issue #589）。
 * もやもやした光は使わない（閃光は板の形の 2 フレームだけ、火花は硬い三角）。
 */
const D = 1740;
const GROW = 240;       // 矢印が伸び切る → 盤の印に引き継ぐ
const DIM_IN = 200;
const SLIDE = 240;      // パネルが滑り込み始める
const HIT = 440;        // パネルが止まる
const STOP = 100;       // ヒットストップ
const POINT_IN = 560;   // 向きの矢じりが飛び始める
const POINT_HIT = 640;  // 向きの矢じりが刺さる
const SHRINK = 1420;    // 縮み始める
const SETTLE = 1700;    // 小さいパネルの場所に着く → 盤の印に引き継ぐ

/** 左右のパネルの間（向きの矢じりが入る） */
const GAP = 140;
/** 向きの矢じりの大きさ（矢じりの画像 351×331） */
const POINT_W = 270;
const POINT_H = POINT_W * (331 / 351);

/** ヒットストップの時間の写し（止めの間は HIT で止まる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));
/** パネルがどちら側か（-1 = 左 / +1 = 右）。攻撃する人の側に攻撃モンスター */
const sideOf = (payload, role) => {
  const a = payload?.player === 1 ? 1 : -1;
  return role === 'attacker' ? a : -a;
};

/** 盤面からカードの中身（code・ATK / DEF・レベル・表示形式・表裏） */
/** パネルに出すもの（攻撃側と、直接攻撃でなければ対象）。`color` / `name` は描く側が足す */
function panelsOf(payload, direct) {
  const p = payload?.player === 1 ? 1 : 0;
  const out = [{ role: 'attacker', who: payload.attacker, spec: panelSpec(payload.attacker, { attacker: true, player: p, side: sideOf(payload, 'attacker') }) }];
  if (!direct) {
    out.push({ role: 'target', who: payload.target, spec: panelSpec(payload.target, { attacker: false, player: 1 - p, side: sideOf(payload, 'target') }) });
  }
  return out;
}

/** 攻撃宣言の組み立て。`direct` = 直接攻撃（対象のパネルが無い） */
export const attackSpec = (direct) => ({
  label: '攻撃宣言',
  color: '#ff3b3b',
  duration: () => D,
  /*
   * **矢印と小さいパネルは盤に置く**（帯が終わっても残る）。前の攻撃のものは頭で消す（1 つの攻撃ぶんだけ出す）。
   * 置く時刻は、帯の中の矢印が伸び切ったところ・大きいパネルが小さいパネルの場所に着いたところ（同じ描き方なので継ぎ目は見えない）。
   */
  clears: () => [{ mark: 'attackArrow' }, { mark: 'battlePanel' }],
  places: ({ payload }) => (payload?.attacker?.uid ? [
    {
      mark: 'attackArrow',
      key: payload.attacker.uid,
      at: { zone: payload.attacker.zone },
      atMs: GROW,
      data: {
        fromUid: payload.attacker.uid,
        fromZone: payload.attacker.zone,
        toUid: payload.target?.uid ?? null,
        toZone: payload.target?.zone ?? null,
        direct: direct,
        player: payload.attacker.player === 1 ? 1 : 0,
      },
    },
    ...panelsOf(payload, direct).map(({ role, who, spec }) => ({
      // uid と役を持たせて、盤に残った後も**今の盤面の値**で描き直す（ユーザー 2026-09-27）
      mark: 'battlePanel', key: role, at: { zone: who.zone }, atMs: SETTLE, data: { spec, uid: who.uid ?? null, attacker: role === 'attacker' },
    })),
  ] : []),
  sounds: () => [
    { at: 0, name: 'attack_decl' },
    { at: HIT - 10, name: 'set_card', gain: 0.9 },
    { at: POINT_HIT - 10, name: 'attack', gain: 0.75 },
  ],
  scene: ['reset',
    {
      ms: HIT,
      grade: {
        contrast: { keys: [{ ms: 0, value: 1.35 }, { ms: STOP, value: 1.35, ease: 'hold' }, { ms: STOP + 1, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0.013 }, { ms: STOP + 100, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: HIT + STOP, shake: { amp: 24, freq: 26, decayMs: 260 } },
    { ms: POINT_HIT, shake: { amp: 14, freq: 30, decayMs: 200 } }],
  hud: ({ payload, localMs }) => (direct
    ? [{ anchor: 'life', player: payload.player === 1 ? 0 : 1, shake: pulse(localMs, { at: POINT_HIT, inMs: 10, hold: 60, outMs: 200 }), phase: localMs }]
    : []),

  layers: [
    {
      id: 'arrow',
      label: '盤の矢印（伸びる間。伸び切ったら盤の印が引き継ぐ）',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const zq = (k) => ctx.stage?.field?.zoneQuad?.(k) ?? null;
        const ends = arrowEnds(zq, {
          fromZone: payload.attacker?.zone, toZone: payload.target?.zone, direct: direct, player: payload.player === 1 ? 1 : 0,
        });
        if (!ends) return { root };
        const color = playerColor(ctx, payload.player);
        // 部品の画像（柄・矢じり）は盤の印と同じもの。描く順は arrowPrims の並び（柄 → 矢じり）
        const meshes = arrowPrims(ends, color).map((p, i) => add(root, sprite(ctx, ctx.kit.urlTexture(ARROW_PARTS[p.part]), 1, 1), 1 + i * 0.01));
        return {
          root,
          update: ({ localMs: t }) => {
            const grow = tween(t, 0, GROW - 40, 'power2_in');
            const on = t < GROW;
            arrowPrims(ends, color, { grow }).forEach((p, i) => {
              const m = meshes[i];
              place(m, p.cx, p.cy, { sx: Math.max(0.001, p.w), sy: p.h, rot: p.rot });
              fade(m, on && !p.hidden ? 1 : 0);
            });
          },
        };
      },
    },
    {
      id: 'versus',
      label: '対戦の表示（パネルと向きの矢じり。縮んで小さいパネルへ）',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const { w: W, h: H } = canvasOf(ctx);
        const { payload } = ctx;
        const root = new THREE.Group();
        const rand = seeded(`${ctx.seed}:attack`);
        const zq = (k) => ctx.stage?.field?.zoneQuad?.(k) ?? null;
        const aSide = sideOf(payload, 'attacker');
        /** 向き（攻撃側 → 対象。+1 = 右へ） */
        const dir = -aSide;

        const dim = add(root, place(fill(ctx, '#040406', W, H), W / 2, H / 2), 0);
        // canvas の中心の高さ（枠の中心は名前の段のぶん下）
        const CY = H * 0.56 - PANEL.nameH / 2;
        const frameCY = CY + PANEL.nameH / 2;

        /** 1 枚のパネル: canvas で描いた 2 層（base / small）を貼った板と、叩きつけの閃光 */
        const makePanel = ({ role, who, spec: s0 }, order) => {
          const spec = { ...s0, color: playerColor(ctx, s0.player), name: cardName(ctx, s0.code) };
          const art = faceArt(ctx, spec);
          const imgs = { art };
          const g = new THREE.Group();
          add(root, g, order);
          const layer = (name, o) => {
            const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
            if (c) { c.width = PANEL.w; c.height = Math.ceil(PANEL.h); }
            const tex = c ? kit.textureFromCanvas(c) : null;
            const paint = () => {
              const g2 = c?.getContext?.('2d');
              if (!g2) return;
              g2.setTransform(1, 0, 0, 1, 0, 0);
              g2.clearRect(0, 0, c.width, c.height);
              paintPanel(g2, spec, name, imgs);
              if (tex) tex.needsUpdate = true;
            };
            paint();
            const mesh = kit.quad(kit.plain(tex, 1), PANEL.w, Math.ceil(PANEL.h));
            mesh.renderOrder = order + o;
            g.add(mesh);
            return { mesh, paint };
          };
          const base = layer('base', 0.01);
          const small = layer('small', 0.02);
          const flash = sprite(ctx, kit.urlTexture(FRAME_URL), PANEL.w, PANEL.frameH, { blend: 'add' });
          flash.position.set(0, PANEL.nameH / 2, 0);
          flash.scale.set(spec.side === 1 ? -1 : 1, 1, 1);
          flash.renderOrder = order + 0.03;
          g.add(flash);
          const mini = miniBox(zq, spec.player, canvasOf(ctx));
          return {
            role, who, spec, g, base, small, flash, imgs, side: spec.side,
            ready: JSON.stringify(panelReady(imgs)),
            home: [W / 2 + spec.side * (PANEL.w / 2 + GAP / 2), CY],
            mini: [mini.x + mini.w / 2, mini.y + mini.h / 2, mini.scale],
          };
        };
        const panels = panelsOf(payload, direct).map((x, i) => makePanel(x, 10 + i));

        // 滑り込むときの硬い線（パネルごとに 3 本）
        const trails = panels.map(() => [-0.3, 0.05, 0.34].map((k, i) => ({ m: add(root, fill(ctx, i === 1 ? '#ffd21a' : '#ffffff', 1, 1), 9), k })));

        /*
         * --- パネルより手前に出すもの（向きの矢じり・火花・閃光）。パネルは renderOrder 付きの group なので、
         * 手前のものも group に入れて group の renderOrder で並べる（group の外に置くとパネルの下に描かれる）
         */
        const top = new THREE.Group();
        add(root, top, 20);
        // 向きの矢じり（盤の矢印と同じ画像）。飛ぶ間の硬い線と、刺さった所の火花
        const speed = [-0.22, 0, 0.22].map((k) => ({ m: add(top, fill(ctx, '#ffffff', 1, 1), 0), k }));
        const point = add(top, sprite(ctx, kit.urlTexture(ARROW_PARTS.head), POINT_W, POINT_H), 1);
        const pointFlash = add(top, sprite(ctx, kit.urlTexture(ARROW_PARTS.head), POINT_W, POINT_H, { blend: 'add' }), 2);
        const sparkCols = ['#ffd21a', '#fff3b0', '#ffb020', '#ffffff'];
        const sparks = Array.from({ length: 22 }, (_, i) => {
          const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
          m.material.color?.set?.(sparkCols[i % sparkCols.length]);
          add(top, m, 3);
          const big = i < 8;
          // 前（対象の側）へ扇に散る
          const a = (dir === 1 ? 0 : Math.PI) + (rand() - 0.5) * Math.PI * 1.3;
          return {
            m, a, len: big ? 110 + rand() * 80 : 45 + rand() * 50, w: big ? 14 + rand() * 8 : 6 + rand() * 6,
            speed: 800 + rand() * 1000, r0: 20 + rand() * 30, life: 170 + rand() * 120,
          };
        });
        const tipX = W / 2 + dir * POINT_W * 0.5;

        const flash = add(top, place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), W / 2, H / 2), 4);

        return {
          root,
          update: ({ localMs: t }) => {
            const u = hs(t);
            const shown = t < SETTLE;
            fade(dim, 0.55 * seg(t, DIM_IN, DIM_IN + 60) * (1 - seg(t, SHRINK, SHRINK + 220)));

            // パネル: 加速して滑り込み、ぶつかって止まる → 反動 → 向きの矢じりで踏み込む / 押される → 縮んで小さいパネルの場所へ
            const inK = tween(t, SLIDE, HIT, 'power2_in');
            const recoil = t >= HIT ? Math.sin(seg(u, HIT, HIT + 90) * Math.PI) * (1 - seg(u, HIT, HIT + 90)) : 0;
            const lunge = tween(t, POINT_IN - 40, POINT_HIT, 'power2_in') * (1 - tween(t, POINT_HIT + 60, POINT_HIT + 360, 'ease_out'));
            const pushed = t >= POINT_HIT ? Math.sin(seg(t, POINT_HIT, POINT_HIT + 140) * Math.PI) * (1 - seg(t, POINT_HIT, POINT_HIT + 140)) : 0;
            const sk = tween(t, SHRINK, SETTLE, 'ease_in_out');
            panels.forEach((pn, i) => {
              // 画像が後から読めたら描き直す
              const ready = JSON.stringify(panelReady(pn.imgs));
              if (ready !== pn.ready) { pn.ready = ready; pn.base.paint(); pn.small.paint(); }
              const [hx, hy] = pn.home;
              const startX = hx + pn.side * (W / 2 + PANEL.w * 0.2);
              let x = startX + (hx - startX) * inK + pn.side * 14 * recoil;
              x += pn.role === 'attacker' ? dir * 36 * lunge : dir * 22 * pushed;
              const [mx, my, ms] = pn.mini;
              place(pn.g, x + (mx - x) * sk, hy + (my - hy) * sk, { s: 1 + (ms - 1) * sk });
              fade(pn.g, shown && t >= SLIDE ? 1 : 0);
              fade(pn.small.mesh, shown && t >= SLIDE ? 1 : 0);
              fade(pn.flash, t >= HIT && t < HIT + 34 ? 0.5 : 0);
              // 硬い線: パネルの後ろから入ってきた側へ伸び、止まったら縮む
              const len = t < HIT ? W * 0.45 * seg(t, SLIDE, SLIDE + 50) : W * 0.45 * (1 - seg(u, HIT, HIT + 80));
              trails[i].forEach(({ m, k }) => {
                const tail = x + pn.side * PANEL.w / 2;
                place(m, tail + pn.side * len / 2, frameCY + k * PANEL.frameH, { sx: Math.max(0.001, len), sy: k === 0.05 ? 6 : 3 });
                fade(m, shown && t >= SLIDE && len > 1 ? 0.9 : 0);
              });
            });

            // 向きの矢じり: 攻撃側のパネルの上から対象の側へ加速して飛び、2 枚の間に刺さる → 見得 → 縦に潰れて消える
            const pk = tween(t, POINT_IN, POINT_HIT, 'power2_in');
            const px = W / 2 - dir * 420 * (1 - pk);
            const bounce = t >= POINT_HIT ? Math.sin(seg(t, POINT_HIT, POINT_HIT + 90) * Math.PI) * (1 - seg(t, POINT_HIT, POINT_HIT + 90)) : 0;
            const psq = tween(t, SHRINK, SHRINK + 120, 'power3_in');
            const ps = (1.5 - 0.5 * pk) * (1 - 0.08 * bounce);
            place(point, px - dir * 10 * bounce, frameCY, { sx: dir * ps * (1 + 0.4 * psq), sy: ps * Math.max(0.001, 1 - psq) });
            place(pointFlash, px, frameCY, { sx: dir * ps, sy: ps });
            fade(point, t >= POINT_IN && t < SHRINK + 120 ? 1 : 0);
            fade(pointFlash, t >= POINT_HIT && t < POINT_HIT + 34 ? 0.6 : 0);
            const trail = t < POINT_HIT ? 380 * pk : 380 * (1 - seg(t, POINT_HIT, POINT_HIT + 80));
            speed.forEach(({ m, k }) => {
              place(m, px - dir * (POINT_W * 0.4 + trail / 2), frameCY + k * POINT_H, { sx: Math.max(0.001, trail), sy: k === 0 ? 6 : 3 });
              fade(m, t >= POINT_IN && trail > 1 ? 0.9 : 0);
            });

            // 火花（刺さった矢じりの先から前へ）
            const since = Math.max(0, t - POINT_HIT);
            sparks.forEach((s) => {
              const k = since / s.life;
              if (t < POINT_HIT || k >= 1) { fade(s.m, 0); return; }
              const r = s.r0 + s.speed * (since / 1000) * (1 - 0.45 * k);
              const l = s.len * (1 - k);
              place(s.m, tipX + Math.cos(s.a) * (r + l / 2), frameCY + Math.sin(s.a) * (r + l / 2), { sx: Math.max(0.001, l), sy: s.w * (1 - 0.5 * k), rot: s.a });
              fade(s.m, 1);
            });

            // 閃光（パネルがぶつかった頭の 2 フレームだけ）
            fade(flash, t >= HIT && t < HIT + 34 ? 0.12 : 0);
          },
        };
      },
    },
  ],
});
